// Registratieformulieren - sjabloon-seed + helpers
// Veldtypes: tekst, aantal, temp, tijd, paraaf
// Sectietypes: checklist | velden | daygrid | afwijkingen | lotcodes | weeg
// Moment: "start" | "halverwege" | "eind" | ""  (leeg = algemeen)

export const REG_MOMENTEN = [
  { key: "", label: "Algemeen" },
  { key: "start", label: "Bij opstart" },
  { key: "halverwege", label: "Halverwege" },
  { key: "eind", label: "Einde productie" },
];

export const REG_DAGEN = ["ma", "di", "wo", "do", "vr"];
export const REG_DAG_LABEL = { ma: "Ma", di: "Di", wo: "Wo", do: "Do", vr: "Vr" };

let _c = 0;
const uid = () => `reg${Date.now()}_${_c++}_${Math.random().toString(36).slice(2, 7)}`;

export function regWeek(dateStr) {
  const d = new Date(String(dateStr || "") + "T00:00:00");
  if (isNaN(d)) return 0;
  const t = new Date(d);
  t.setHours(0, 0, 0, 0);
  t.setDate(t.getDate() + 3 - ((t.getDay() + 6) % 7));
  const w1 = new Date(t.getFullYear(), 0, 4);
  return 1 + Math.round(((t - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7);
}

export function regTemplates(data) {
  const arr = (data?.regFormSjablonen || []).filter((t) => t && typeof t === "object");
  return arr.length ? arr : REG_SEED;
}

export function regInzendingen(data) {
  return (data?.regFormInzendingen || []).filter((s) => s && typeof s === "object");
}

// Hoort een inzending bij een auditdag?
export function inzendingOpDag(sub, tpl, dag) {
  if (!tpl) return false;
  if (tpl.periode === "week") return String(sub.week) === String(regWeek(dag));
  return sub.datum === dag;
}

export function subMatches(sub, tpl, ctx) {
  if (String(sub.sjabloonId) !== String(tpl.id)) return false;
  if (tpl.periode === "week") { if (String(sub.week) !== String(ctx.week)) return false; }
  else if (sub.datum !== ctx.datum) return false;
  if (tpl.perLijn && String(sub.lijn || "") !== String(ctx.lijn || "")) return false;
  if (tpl.perMedewerker && String(sub.medewerker || "") !== String(ctx.medewerker || "")) return false;
  return true;
}

export function nieuweInzending(tpl, ctx, user) {
  return {
    id: uid(),
    sjabloonId: tpl.id,
    code: tpl.code,
    titel: tpl.titel,
    periode: tpl.periode,
    datum: ctx.datum || "",
    week: tpl.periode === "week" ? regWeek(ctx.datum) : 0,
    lijn: tpl.perLijn ? (ctx.lijn || "") : "",
    medewerker: tpl.perMedewerker ? (ctx.medewerker || "") : "",
    status: "open",
    data: {},
    aangemaaktDoor: user?.name || user?.email || "",
    updatedAt: new Date().toISOString(),
    afgerondDoor: "",
    afgerondTs: "",
  };
}

// Alle inzendingen die bij een sjabloon + context (datum/week + lijn + medewerker) horen.
// Anders dan subMatches (die 1 inzending zoekt) geeft dit ALLE inzendingen terug,
// zodat er meerdere formulieren per lijn/dag kunnen bestaan (bijv. 2-3 producten op 1 lijn).
export function subsForContext(data, tpl, ctx) {
  return regInzendingen(data).filter((s) => {
    if (s.verwijderd) return false;
    if (s.bron === "kalibratie") return false;
    if (String(s.sjabloonId) !== String(tpl.id)) return false;
    if (tpl.periode === "week") { if (String(s.week) !== String(ctx.week)) return false; }
    else if (s.datum !== ctx.datum) return false;
    if (tpl.perLijn && String(s.lijn || "") !== String(ctx.lijn || "")) return false;
    if (tpl.perMedewerker && String(s.medewerker || "") !== String(ctx.medewerker || "")) return false;
    return true;
  }).sort((a, b) => String(a.updatedAt || "").localeCompare(String(b.updatedAt || "")));
}

// Leest het gekozen product uit een inzending (eerste velden-sectie met vt "product").
export function subProduct(sub, tpl) {
  const D = sub?.data || {};
  let prod = "";
  (tpl?.secties || []).forEach((sec) => { if (sec.type === "velden") (sec.velden || []).forEach((v) => { if (v.vt === "product" && (D[sec.id] || {})[v.id]) prod = (D[sec.id])[v.id]; }); });
  return prod;
}

// ---- SEED: de 4 startformulieren ----
export const REG_SEED = [
  {
    id: "rp25-f01", code: "RP25-F01", titel: "Start- en eindcontrole productielijn",
    actief: true, periode: "dag", perLijn: true, perMedewerker: false, lijnOpties: ["Lijn 1", "Lijn 2", "Lijn 3"],
    secties: [
      { id: "algemeen", type: "velden", titel: "Algemeen", moment: "start", velden: [
        { id: "product", label: "Product", vt: "product" },
        { id: "operator", label: "Operator", vt: "tekst" },
      ] },
      { id: "startcontrole", type: "checklist", titel: "Startcontrole", moment: "start", items: [
        { id: "s1", label: "Lijn is schoon" },
        { id: "s2", label: "Machines correct gemonteerd, onderdelen compleet en heel" },
        { id: "s3", label: "Oven, frituur en vriezer correct ingesteld en op temperatuur" },
        { id: "s4", label: "Metaaldetector werkend" },
        { id: "s5", label: "Etiketten gecontroleerd en correct" },
        { id: "s6", label: "Medewerkers dragen juiste kleur haarnetje (Paars: overal, Groen: gaar, Blauw: rauw)" },
      ] },
      { id: "vrijgave_start", type: "velden", titel: "Vrijgave startcontrole (productieleider)", moment: "start", velden: [
        { id: "vs", label: "Vrijgave startcontrole", vt: "paraaf" },
      ] },
      { id: "eindcontrole", type: "checklist", titel: "Eindcontrole", moment: "eind", items: [
        { id: "e1", label: "Lijn opgeruimd" },
        { id: "e2", label: "Machines leeggedraaid (bloem verwijderd)" },
        { id: "e3", label: "Vleesresten verwijderd" },
        { id: "e4", label: "Onderdelen gedemonteerd en in rek geplaatst" },
        { id: "e5", label: "Machines op schoonmaakstand" },
        { id: "e6", label: "Frituur en oven uitgeschakeld" },
        { id: "e7", label: "Vriezer op ontdooistand indien nodig" },
        { id: "e8", label: "Yamato-weger onderdelen gedemonteerd" },
        { id: "e9", label: "Verpakker VH onder bordes verwijderd en gereinigd" },
      ] },
      { id: "vrijgave_eind", type: "velden", titel: "Vrijgave eindcontrole (productieleider)", moment: "eind", velden: [
        { id: "ve", label: "Vrijgave eindcontrole", vt: "paraaf" },
      ] },
      { id: "afwijkingen", type: "afwijkingen", titel: "Afwijkingen & correctieve acties", moment: "eind" },
    ],
  },
  {
    id: "op07-f01", code: "OP07-F01", titel: "Gereedschap productielijn (1, 2 & 3)",
    actief: true, periode: "week", perLijn: true, perMedewerker: false, lijnOpties: ["Lijn 1", "Lijn 2", "Lijn 3"],
    secties: [
      { id: "alg", type: "velden", titel: "Algemeen", moment: "", velden: [
        { id: "verantwoordelijke", label: "Verantwoordelijke medewerker", vt: "tekst" },
      ] },
      { id: "gereedschap", type: "daygrid", titel: "Gereedschapscontrole (aanwezig per dag)", rijen: [
        { id: "g13", label: "Steeksleutel 13 (2x)", ct: "check" },
        { id: "g15", label: "Steeksleutel 15 (1x)", ct: "check" },
        { id: "g17", label: "Steeksleutel 17 (1x)", ct: "check" },
        { id: "g19", label: "Steeksleutel 19 (1x)", ct: "check" },
        { id: "g22", label: "Steeksleutel 22 (2x)", ct: "check" },
        { id: "g24", label: "Steeksleutel 24 (1x)", ct: "check" },
      ] },
      { id: "uitgifte", type: "velden", titel: "Staat & afwijkingen", moment: "", velden: [
        { id: "staat", label: "Staat bij uitgifte (OK / Defect)", vt: "tekst" },
        { id: "incidenten", label: "Afwijkingen / incidenten", vt: "tekst" },
      ] },
      { id: "aftekenen", type: "velden", titel: "Aftekenen", moment: "eind", velden: [
        { id: "medewerker", label: "Medewerker productie", vt: "paraaf" },
        { id: "supervisor", label: "Supervisor / TD", vt: "paraaf" },
      ] },
    ],
  },
  {
    id: "op05-f02", code: "OP05-F02", titel: "Messen voorbereiding (1 - 5 medewerkers)",
    actief: true, periode: "week", perLijn: false, perMedewerker: true,
    secties: [
      { id: "alg", type: "velden", titel: "Algemeen", moment: "", velden: [
        { id: "totaalMessen", label: "Totaal aantal messen in deze afdeling (vast aantal)", vt: "aantal" },
      ] },
      { id: "messen", type: "daygrid", titel: "Messen & sterilisator per dag", rijen: [
        { id: "uit", label: "Aantal uitgegeven", ct: "aantal" },
        { id: "in", label: "Aantal ingenomen", ct: "aantal" },
        { id: "st1", label: "Sterilisator >82°C — voor opstart", ct: "check" },
        { id: "st2", label: "Sterilisator >82°C — halverwege", ct: "check" },
        { id: "st3", label: "Sterilisator >82°C — voor einde productie", ct: "check" },
      ] },
      { id: "aftekenen", type: "velden", titel: "Aftekenen", moment: "eind", velden: [
        { id: "voorman", label: "Handtekening voorman voorbereiding", vt: "paraaf" },
        { id: "productieleider", label: "Handtekening productieleider", vt: "paraaf" },
      ] },
    ],
  },
  {
    id: "op05-f01", code: "OP05-F01", titel: "Messen voor zakken openen (Lijn 1, 2 & 3)",
    actief: true, periode: "week", perLijn: true, perMedewerker: true, lijnOpties: ["Lijn 1", "Lijn 2", "Lijn 3"],
    secties: [
      { id: "messen", type: "daygrid", titel: "Messen & sterilisator per dag", rijen: [
        { id: "uit", label: "Aantal uitgegeven", ct: "aantal" },
        { id: "in", label: "Aantal ingenomen", ct: "aantal" },
        { id: "st1", label: "Sterilisator >82°C — voor opstart", ct: "check" },
        { id: "st2", label: "Sterilisator >82°C — halverwege", ct: "check" },
        { id: "st3", label: "Sterilisator >82°C — voor einde productie", ct: "check" },
      ] },
      { id: "aftekenen", type: "velden", titel: "Aftekenen", moment: "eind", velden: [
        { id: "voorman", label: "Handtekening voorman voorbereiding", vt: "paraaf" },
        { id: "productieleider", label: "Handtekening productieleider", vt: "paraaf" },
      ] },
    ],
  },
];

// ---- Kant-en-klaar productieformulier ("blad 2": temp / metaal / olie + lotcodes) ----
// Grenswaarden (min/max) zijn door de beheerder aan te passen in de formulier-bouwer.
const _meting = (mid, moment, titel) => ({
  id: mid, type: "velden", titel, moment, velden: [
    { id: "temp_oven", label: "Kerntemperatuur na oven (°C)", vt: "temp", min: 75 },
    { id: "temp_vries", label: "Temperatuur na vriezer (°C)", vt: "temp", max: -18 },
    ...(moment === "start" ? [{ id: "olie", label: "Olie% frituur (alleen gecoat/frituur)", vt: "aantal" }] : []),
  ],
});
const _metaal = (mid, moment, titel) => ({
  id: mid, type: "checklist", titel, moment, items: [
    { id: "fe", label: "Fe (ijzer) teststaaf — detector slaat aan" },
    { id: "nonfe", label: "Non-Fe teststaaf — detector slaat aan" },
    { id: "rvs", label: "RVS teststaaf — detector slaat aan" },
  ],
});

export const PRODUCTIE_REG_TEMPLATE = {
  id: "op08-f01", code: "OP08-F01", titel: "Productiecontrole — temperatuur, metaal, olie & lotcodes",
  actief: true, periode: "dag", perLijn: true, perMedewerker: false, lijnOpties: ["Lijn 1", "Lijn 2", "Lijn 3"],
  prefab: true,
  secties: [
    { id: "alg", type: "velden", titel: "Algemeen", moment: "start", velden: [
      { id: "product", label: "Product", vt: "product" },
      { id: "operator", label: "Operator", vt: "tekst" },
    ] },
    { id: "lot", type: "lotcodes", titel: "Materialen & lotcodes (traceability)", moment: "start", rijen: [
      { id: "hoofd", label: "Halffabricaat (gemarineerd product — kies lotcode uit voorraad)" },
      { id: "predust", label: "Predust" },
      { id: "batter", label: "Battermix" },
      { id: "breader", label: "Breader" },
    ] },
    _meting("meting_start", "start", "Meting bij opstart"),
    _metaal("metaal_start", "start", "Metaaldetectie bij opstart (alle 3 moeten aanslaan)"),
    _meting("meting_half", "halverwege", "Meting halverwege"),
    _metaal("metaal_half", "halverwege", "Metaaldetectie halverwege"),
    _meting("meting_eind", "eind", "Meting einde productie"),
    _metaal("metaal_eind", "eind", "Metaaldetectie einde productie"),
    { id: "afw", type: "afwijkingen", titel: "Afwijkingen & correctieve acties", moment: "eind" },
    { id: "vrijgave", type: "velden", titel: "Vrijgave / aftekenen (einde dag)", moment: "eind", velden: [
      { id: "paraaf", label: "Afgetekend door productie", vt: "paraaf" },
    ] },
  ],
};

REG_SEED.push(PRODUCTIE_REG_TEMPLATE);

// Bouwt de lijst beschikbare partijen/tracecodes uit de live voorraad (grondstof + hulpstof + halffabricaat).
// Halffabricaat-runs krijgen automatisch een lotcode HF-JJJJMMDD-NN (deterministisch, niet opgeslagen).
export function buildTraceOpties(data) {
  const d = data || {};
  const num = (x) => { const n = Number(x); return isNaN(n) ? 0 : n; };
  const entries = d.entries || [];
  const runs = (d.halffabRuns || []).filter((r) => r && typeof r === "object");
  const out = [];
  // Resterende kg per grondstof-partij: naam-verbruik (entries + halffab zonder expeditieId) FIFO over de partijen (oudste eerst), plus expliciete partij-afboekingen. Zo verdwijnen opgebruikte oude partijen uit de keuzelijst.
  const explicitOf = (b) => entries.reduce((s, e) => s + (e && String(e.expeditieId) === String(b.id) ? Math.max(num(e.grondstofKg) - num(e.restantKg), 0) : 0), 0)
    + runs.reduce((s, r) => s + (r.verbruik || []).reduce((s2, v) => s2 + (v && String(v.expeditieId) === String(b.id) ? num(v.kg) : 0), 0), 0);
  const unattributedOf = (naam) => entries.reduce((s, e) => s + (e && e.grondstof === naam && !e.expeditieId ? Math.max(num(e.grondstofKg) - num(e.restantKg), 0) : 0), 0)
    + runs.reduce((s, r) => s + (r.verbruik || []).reduce((s2, v) => s2 + (v && v.categorie === "Grondstof" && v.naam === naam && !v.expeditieId ? num(v.kg) : 0), 0), 0);
  const perNaam = {};
  (d.expeditie || []).filter((b) => b && typeof b === "object" && b.grondstof && (b.eigenTracecode || b.batchnr)).forEach((b) => { (perNaam[b.grondstof] = perNaam[b.grondstof] || []).push(b); });
  Object.keys(perNaam).forEach((naam) => {
    const batches = perNaam[naam].slice().sort((a, z) => (String(a.datum || "") < String(z.datum || "") ? -1 : 1));
    let pool = unattributedOf(naam);
    batches.forEach((b) => {
      let rest = num(b.kg) - explicitOf(b);
      const take = Math.min(Math.max(rest, 0), Math.max(pool, 0));
      rest -= take; pool -= take;
      if (rest <= 0.001) return; // alleen partijen die nog echt op voorraad staan
      const code = b.eigenTracecode || b.batchnr;
      out.push({ categorie: "Grondstof", naam: String(b.grondstof), code: String(code), kg: rest, datum: b.datum || "", label: `${b.grondstof} · ${code} · rest ${Math.round(rest)}kg` });
    });
  });
  (d.hulpstofInkoop || []).filter((h) => h && typeof h === "object" && h.batchnr).forEach((h) => {
    out.push({ categorie: "Hulpstof", naam: String(h.hulpstof), code: String(h.batchnr), kg: num(h.kg), datum: h.datum || "", label: `${h.hulpstof} · ${h.batchnr}${h.kg ? ` · ${Math.round(num(h.kg))}kg` : ""}` });
  });
  const seq = {}; const lotOf = new Map();
  [...runs].sort((a, b) => (String(a.datum) < String(b.datum) ? -1 : String(a.datum) > String(b.datum) ? 1 : (a.id < b.id ? -1 : 1))).forEach((r) => {
    const dc = String(r.datum || "").replace(/-/g, "");
    seq[dc] = (seq[dc] || 0) + 1;
    lotOf.set(r, r.lotcode || `HF-${dc}-${String(seq[dc]).padStart(2, "0")}`);
  });
  // Halffabricaat: GEEN FIFO. Verbruik wordt op NAAM-niveau bijgehouden (entries[].grondstof met
  // grondstofKg−restantKg, plus gebruik als halffab-ingredient in een andere run) — precies zoals de
  // invoer-/restant-flow al werkt. Daarom tonen we ALLE lotcodes van een halffabricaat zolang dat
  // halffabricaat nog voorraad heeft; zodra het volledig op is, verdwijnen al zijn lotcodes. De
  // operator kiest dus zelf welk fysiek batchje hij pakt.
  const hfCorr = d.halffabCorrecties || {};
  const hfOutput = (r) => { const g = (r.verbruik || []).reduce((s, v) => s + (v && v.categorie === "Grondstof" ? num(v.kg) : 0), 0); return g > 0 ? g : num(r.batch); };
  const hfProduced = (naam) => runs.reduce((s, r) => s + (r.recept === naam ? hfOutput(r) : 0), 0);
  const hfConsumed = (naam) => entries.reduce((s, e) => s + (e && e.grondstof === naam ? Math.max(num(e.grondstofKg) - num(e.restantKg), 0) : 0), 0)
    + runs.reduce((s, r) => s + (r.verbruik || []).reduce((s2, v) => s2 + (v && v.categorie === "Halffabricaat" && v.naam === naam ? num(v.kg) : 0), 0), 0);
  const hfRestCache = {};
  const hfRest = (naam) => { if (hfRestCache[naam] == null) hfRestCache[naam] = hfProduced(naam) - hfConsumed(naam) + num(hfCorr[naam]); return hfRestCache[naam]; };
  runs.forEach((r) => {
    if (!r.recept) return;
    const rest = hfRest(r.recept);
    if (rest <= 0.001) return; // halffabricaat volledig op → alle lotcodes verbergen
    const code = lotOf.get(r);
    out.push({ categorie: "Halffabricaat", naam: String(r.recept), code: String(code), kg: rest, datum: r.datum || "", label: `${r.recept} · ${code} · nog ${Math.round(rest)}kg op voorraad` });
  });
  out.sort((a, b) => String(b.datum).localeCompare(String(a.datum)));
  return out;
}


// Weeglijst-berekening: gemiddelde per groep + totaalgemiddelde over alle gewogen stuks.
// sec.groepen = [{id,label,aantal}]; leeg => één groep met sec.aantal stuks.
export function weegBerekening(sec, secData) {
  const num = (x) => { const n = parseFloat(String(x == null ? "" : x).replace(",", ".")); return isNaN(n) ? null : n; };
  const groepen = (sec && sec.groepen && sec.groepen.length) ? sec.groepen : [{ id: "_", label: "Gewichten", aantal: (sec && sec.aantal) || 10, doel: sec && sec.doel, marge: sec && sec.marge }];
  const data = secData || {};
  let allVals = [];
  const perGroep = groepen.map((g) => {
    const cap = g.aantal || (sec && sec.aantal) || 10;
    const arr = (Array.isArray(data[g.id]) ? data[g.id] : []).slice(0, cap);
    const vals = arr.map(num).filter((v) => v != null);
    allVals = allVals.concat(vals);
    const gem = vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    const doel = (g.doel === "" || g.doel == null) ? null : Number(g.doel);
    const marge = (g.marge === "" || g.marge == null) ? null : Number(g.marge);
    let min = null, max = null, buiten = false;
    if (doel != null && !isNaN(doel) && marge != null && !isNaN(marge)) { min = doel - marge; max = doel + marge; if (gem != null && (gem < min || gem > max)) buiten = true; }
    return { id: g.id, label: g.label, aantal: cap, n: vals.length, gem, doel, marge, min, max, buiten };
  });
  const som = allVals.reduce((s, v) => s + v, 0);
  // Gemengd (bv. plat + dik): 1 stuk per groep telt samen als 1 stuk.
  // Totaalgemiddelde = som van alle gewogen stuks / aantal stuks in de volste groep.
  const gemengd = !!(sec && sec.gemengd) && groepen.length > 1;
  const combinedN = perGroep.reduce((m, g) => Math.max(m, g.n || 0), 0);
  const totaalN = gemengd ? combinedN : allVals.length;
  const totaalGem = gemengd ? (combinedN ? som / combinedN : null) : (allVals.length ? som / allVals.length : null);
  const tDoel = (sec && (sec.totaalDoel === "" || sec.totaalDoel == null)) ? null : Number(sec.totaalDoel);
  const tMarge = (sec && (sec.totaalMarge === "" || sec.totaalMarge == null)) ? null : Number(sec.totaalMarge);
  let totaalMin = null, totaalMax = null, totaalBuiten = false;
  if (gemengd && tDoel != null && !isNaN(tDoel) && tMarge != null && !isNaN(tMarge)) {
    totaalMin = tDoel - tMarge; totaalMax = tDoel + tMarge;
    if (totaalGem != null && (totaalGem < totaalMin || totaalGem > totaalMax)) totaalBuiten = true;
  }
  return { groepen: perGroep, totaalN, totaalGem, gemengd, totaalDoel: tDoel, totaalMarge: tMarge, totaalMin, totaalMax, totaalBuiten };
}

// Bouwt de payload voor de afwijkings-mail bij afronden van een weegformulier.
// Geeft null terug als er GEEN weeg-afwijking is (dan wordt er geen mail verstuurd).
export function buildAfwijkingAlert(sub, tpl) {
  if (!sub || !tpl) return null;
  const num = (x) => { const n = parseFloat(String(x == null ? "" : x).replace(",", ".")); return isNaN(n) ? null : n; };
  const D = sub.data || {};
  const secties = [];
  const overige = [];
  let heeftWeegAfwijking = false;
  (tpl.secties || []).forEach((sec) => {
    const sv = D[sec.id] || {};
    if (sec.type === "weeg") {
      const calc = weegBerekening(sec, sv);
      if (calc.groepen.some((g) => g.buiten) || calc.totaalBuiten) heeftWeegAfwijking = true;
      secties.push({
        titel: sec.titel || "Weging",
        gemengd: !!calc.gemengd,
        eenheid: sec.eenheid || "g",
        totaalN: calc.totaalN || 0,
        totaalGem: calc.totaalGem, totaalDoel: calc.totaalDoel, totaalMarge: calc.totaalMarge,
        totaalMin: calc.totaalMin, totaalMax: calc.totaalMax, totaalBuiten: !!calc.totaalBuiten,
        groepen: calc.groepen.map((g) => ({ label: g.label, n: g.n, gem: g.gem, doel: g.doel, marge: g.marge, min: g.min, max: g.max, buiten: !!g.buiten })),
      });
    } else if (sec.type === "velden") {
      (sec.velden || []).forEach((v) => {
        if (v.vt !== "temp" && v.vt !== "aantal") return;
        const val = num(sv[v.id]); if (val == null) return;
        const mn = v.min ?? v.norm; const mx = v.max;
        if ((mn != null && val < mn) || (mx != null && val > mx)) overige.push(`${sec.titel} – ${v.label}: ${val}${mn != null ? ` (min ${mn})` : ""}${mx != null ? ` (max ${mx})` : ""}`);
      });
    } else if (sec.type === "checklist") {
      (sec.items || []).forEach((it) => { if ((sv[it.id] || {}).status === "niet") overige.push(`${sec.titel} – ${it.label}: niet akkoord`); });
    }
  });
  if (!heeftWeegAfwijking) return null;
  return {
    formulier: sub.titel || "", code: sub.code || "", lijn: sub.lijn || "",
    periodeLabel: sub.periode === "week" ? `week ${sub.week}` : (sub.datum || ""),
    afgerondDoor: sub.afgerondDoor || "", product: subProduct(sub, tpl) || "",
    secties, overige,
  };
}


export const regUid = uid;

// ---- Ontbrekende (verplichte) registraties voor een dag ----
// Geeft een lijst { tpl, lijn } terug van verplichte, actieve sjablonen
// die op deze datum (of week) nog GEEN afgeronde inzending hebben.
export function ontbrekendeRegistraties(data, datum) {
  if (!datum) return [];
  const tpls = regTemplates(data).filter((t) => t && t.actief !== false && t.verplicht);
  const subs = regInzendingen(data);
  const wk = regWeek(datum);
  const out = [];
  tpls.forEach((tpl) => {
    const isWeek = tpl.periode === "week";
    const isDone = (lijn) => subs.some((s) =>
      !s.verwijderd &&
      s.bron !== "kalibratie" &&
      String(s.sjabloonId) === String(tpl.id) &&
      s.status === "afgerond" &&
      (isWeek ? String(s.week) === String(wk) : s.datum === datum) &&
      (tpl.perLijn ? String(s.lijn || "") === String(lijn || "") : true)
    );
    if (tpl.perLijn) {
      (tpl.lijnOpties || []).forEach((lijn) => { if (lijn && !isDone(lijn)) out.push({ tpl, lijn }); });
    } else {
      if (!isDone("")) out.push({ tpl, lijn: "" });
    }
  });
  return out;
}

// ---- Kalibratie-trigger ----
// Een trigger = { id, grondstof, sjabloonId, afdeling, actief, vanaf }.
// Bij het inboeken van een grondstof (expeditie-partij, bewerking "Ontvangen") ontstaat
// automatisch een open kalibratietaak, afgeleid uit de live data (niets extra opgeslagen).
// De taak verdwijnt zodra er een AFGERONDE inzending aan die partij+trigger is gekoppeld.
export function openKalibratieTaken(data) {
  const triggers = (data?.kalibratieTriggers || []).filter((t) => t && typeof t === "object" && t.actief !== false && t.grondstof && t.sjabloonId);
  if (!triggers.length) return [];
  const tpls = regTemplates(data);
  const batches = (data?.expeditie || []).filter((b) => b && typeof b === "object" && (!b.type || b.type === "Grondstof") && b.grondstof);
  const subs = regInzendingen(data);
  const out = [];
  triggers.forEach((tr) => {
    const tpl = tpls.find((t) => String(t.id) === String(tr.sjabloonId));
    if (!tpl || tpl.actief === false) return;
    batches.filter((b) => b.grondstof === tr.grondstof).forEach((b) => {
      if (tr.vanaf && String(b.datum || "") < String(tr.vanaf)) return;
      const done = subs.some((s) => !s.verwijderd && String(s.kalibratieBatchId) === String(b.id) && String(s.kalibratieTriggerId) === String(tr.id) && s.status === "afgerond");
      if (done) return;
      const code = b.eigenTracecode || b.batchnr || "";
      out.push({ id: `kal_${tr.id}_${b.id}`, trigger: tr, tpl, batch: b, grondstof: tr.grondstof, afdeling: tr.afdeling || "", batchCode: code, datum: b.datum || "" });
    });
  });
  out.sort((a, z) => String(z.datum).localeCompare(String(a.datum)));
  return out;
}

// Bouwt een nieuwe inzending voor een kalibratietaak, gekoppeld aan de grondstofpartij.
export function nieuweKalibratieInzending(taak, user) {
  const vandaag = new Date().toISOString().slice(0, 10);
  const sub = nieuweInzending(taak.tpl, { datum: vandaag, week: regWeek(vandaag), lijn: "", medewerker: "" }, user);
  sub.bron = "kalibratie";
  sub.kalibratieBatchId = String(taak.batch.id);
  sub.kalibratieTriggerId = String(taak.trigger.id);
  sub.kalibratieGrondstof = taak.grondstof;
  sub.kalibratieBatchCode = taak.batchCode || "";
  return sub;
}

// Mag een gebruiker (rol/basisrol) een kalibratietaak voor deze afdeling zien?
export function kalibratieZichtbaarVoor(afdeling, role, roleBase) {
  const admin = ["Bedrijfsleiding", "Bedrijfsleiding 2", "QA"];
  if (admin.includes(roleBase) || admin.includes(role)) return true;
  if (!afdeling) return true; // 'Alle afdelingen'
  return String(afdeling) === String(role) || String(afdeling) === String(roleBase);
}
